/**
 * PUDO Dashboard - Main hub for PUDO staff operations
 * Dark Jamaican-themed matching Seller & Driver dashboards
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Package, ArrowLeft, Inbox, Users, Clock, AlertTriangle,
  CheckCircle, Truck, RefreshCw, ChevronRight, MapPin, Settings,
  Navigation, Save, Store, Wallet, DollarSign, Trophy, Star, TrendingUp
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const PudoDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    pudo_id: null,
    incoming_packages: [],
    waiting_packages: [],
    stats: {
      packages_waiting: 0,
      packages_incoming: 0,
      packages_today: 0,
      avg_wait_time_minutes: 0,
      delayed_handoffs: 0
    }
  });
  const [alerts, setAlerts] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [pudoSettings, setPudoSettings] = useState({
    hours: '', address: '', phone: '', latitude: null, longitude: null
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchAlerts();
    fetchPudoSettings();
    fetchEarnings();
    fetchLeaderboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/pudo/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('PUDO staff access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/pudo/alerts');
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const fetchPudoSettings = async () => {
    try {
      const response = await api.get("/business/pudo/settings");
      setPudoSettings({
        hours: response.data.hours || "",
        address: response.data.address || "",
        phone: response.data.phone || "",
        latitude: response.data.latitude || null,
        longitude: response.data.longitude || null
      });
    } catch (error) {
      console.error("Failed to load PUDO settings:", error);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await api.get('/pudo-earnings/my-earnings');
      setEarnings(response.data);
    } catch (error) {
      console.error('Failed to load earnings:', error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await api.get('/pudo/leaderboard');
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  };

  const savePudoSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/business/pudo/settings", pudoSettings);
      toast.success("Settings saved successfully!");
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPudoSettings(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        }));
        toast.success("Location captured!");
        setGettingLocation(false);
      },
      () => {
        toast.error("Failed to get location. Please enable location services.");
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDashboard(), fetchAlerts()]);
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const { stats, incoming_packages, waiting_packages } = dashboardData;

  return (
    <>
      <div className="min-h-screen bg-slate-950 text-white" data-testid="pudo-dashboard">
        {/* Header */}
        <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="sm"
                className="text-slate-400"
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <MapPin className="h-6 w-6 text-amber-400" />
              <span className="text-lg font-bold">PUDO Dashboard</span>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">PUDO</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="text-slate-400"
                data-testid="settings-button"
              >
                <Settings className="h-4 w-4 mr-1" /> Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-slate-400"
                data-testid="refresh-button"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} className="text-slate-400">Sign Out</Button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {/* Alerts */}
          {alerts.length > 0 && (
            <Card className="bg-amber-900/30 border-amber-500/30" data-testid="alerts-section">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-amber-400" />
                  <span className="font-semibold text-amber-400">Attention Required</span>
                </div>
                <div className="space-y-2">
                  {alerts.slice(0, 3).map((alert, idx) => (
                    <div key={idx} className="text-sm text-amber-200 flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${alert.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'}`} />
                      {alert.message}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-grid">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Truck className="h-5 w-5 text-blue-400" />
                  </div>
                  <span className="text-slate-400 text-sm">Incoming</span>
                </div>
                <p className="text-3xl font-bold text-white" data-testid="incoming-count">{stats.packages_incoming}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Package className="h-5 w-5 text-amber-400" />
                  </div>
                  <span className="text-slate-400 text-sm">In Inventory</span>
                </div>
                <p className="text-3xl font-bold text-white" data-testid="waiting-count">{stats.packages_waiting}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                  </div>
                  <span className="text-slate-400 text-sm">Today</span>
                </div>
                <p className="text-3xl font-bold text-white" data-testid="today-count">{stats.packages_today}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-violet-500/20 rounded-lg">
                    <Clock className="h-5 w-5 text-violet-400" />
                  </div>
                  <span className="text-slate-400 text-sm">Avg Wait</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats.avg_wait_time_minutes}<span className="text-lg text-slate-500">m</span></p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="space-y-3" data-testid="quick-actions">
            <h2 className="text-lg font-semibold text-white">Quick Actions</h2>

            <Button
              onClick={() => navigate('/pudo/receive')}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-xl flex items-center justify-between"
              data-testid="receive-package-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/30 rounded-lg">
                  <Inbox className="h-6 w-6" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Receive Package</p>
                  <p className="text-sm text-emerald-200">Scan to intake from driver</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6" />
            </Button>

            <Button
              onClick={() => navigate('/pudo/handoff')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-xl flex items-center justify-between"
              data-testid="handoff-driver-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/30 rounded-lg">
                  <Truck className="h-6 w-6" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Handoff to Driver</p>
                  <p className="text-sm text-blue-200">Release package to next driver</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6" />
            </Button>

            <Button
              onClick={() => navigate('/pudo/release')}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 rounded-xl flex items-center justify-between"
              data-testid="release-customer-btn"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/30 rounded-lg">
                  <Users className="h-6 w-6" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Release to Customer</p>
                  <p className="text-sm text-amber-200">Complete delivery with QR/OTP</p>
                </div>
              </div>
              <ChevronRight className="h-6 w-6" />
            </Button>

            <Button
              onClick={() => navigate('/pudo/inventory')}
              variant="outline"
              className="w-full py-4 rounded-xl flex items-center justify-between border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="view-inventory-btn"
            >
              <div className="flex items-center gap-3">
                <Package className="h-5 w-5 text-slate-400" />
                <span className="font-medium">View Full Inventory</span>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-500" />
            </Button>

            <Button
              onClick={() => navigate('/withdrawals')}
              variant="outline"
              className="w-full py-4 rounded-xl flex items-center justify-between border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="withdraw-btn"
            >
              <div className="flex items-center gap-3">
                <Wallet className="h-5 w-5 text-emerald-400" />
                <span className="font-medium">Withdraw Earnings</span>
              </div>
              <ChevronRight className="h-5 w-5 text-slate-500" />
            </Button>

            {/* Financial Summary */}
            {earnings && (
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700" data-testid="financial-summary">
                <CardContent className="p-5">
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-emerald-400" /> Financial Summary
                  </h2>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                      <p className="text-xs text-slate-400">Available Balance</p>
                      <p className="text-2xl font-bold text-emerald-400">J${earnings.available_balance?.toLocaleString() || '0'}</p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <p className="text-xs text-slate-400">Total Earned</p>
                      <p className="text-2xl font-bold text-blue-400">J${earnings.total_all_time?.toLocaleString() || '0'}</p>
                    </div>
                    <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-3">
                      <p className="text-xs text-slate-400">Direct Delivery ({earnings.rates?.direct_delivery_percent}%)</p>
                      <p className="text-lg font-bold text-violet-400">J${earnings.direct_delivery_total?.toLocaleString() || '0'}</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                      <p className="text-xs text-slate-400">Transit Handling ({earnings.rates?.transit_handling_percent}%)</p>
                      <p className="text-lg font-bold text-amber-400">J${earnings.transit_handling_total?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                  <div className="flex justify-between text-sm text-slate-400 border-t border-slate-700 pt-3">
                    <span>Packages: <strong className="text-white">{earnings.packages_handled}</strong></span>
                    <span>Pending: <strong className="text-amber-400">J${earnings.pending_withdrawals?.toLocaleString() || '0'}</strong></span>
                    <span>Withdrawn: <strong className="text-emerald-400">J${earnings.total_withdrawn?.toLocaleString() || '0'}</strong></span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* PUDO Leaderboard */}
            {leaderboard && (
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700" data-testid="pudo-leaderboard">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-400" /> PUDO Leaderboard
                    </h2>
                    <Badge className="bg-slate-700 text-slate-300 border-slate-600">
                      {leaderboard.total_pudos} PUDOs
                    </Badge>
                  </div>

                  {/* My Rank Card */}
                  {leaderboard.my_pudo && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-amber-400">#{leaderboard.my_rank}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-white">{leaderboard.my_pudo.name}</p>
                            <p className="text-xs text-slate-400">Your Rank</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-amber-400">{leaderboard.my_pudo.packages_handled}</p>
                          <p className="text-xs text-slate-400">packages</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Leaderboard List */}
                  <div className="space-y-2">
                    {leaderboard.leaderboard.slice(0, 10).map((entry) => {
                      const isMe = entry.owner_id === user?.id;
                      const medalColors = ['text-amber-400', 'text-slate-300', 'text-orange-400'];
                      return (
                        <div
                          key={entry.pudo_id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            isMe ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-slate-800/50'
                          }`}
                          data-testid={`leaderboard-entry-${entry.rank}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 flex items-center justify-center">
                              {entry.rank <= 3 ? (
                                <Trophy className={`h-5 w-5 ${medalColors[entry.rank - 1]}`} />
                              ) : (
                                <span className="text-sm font-bold text-slate-500">#{entry.rank}</span>
                              )}
                            </div>
                            <div>
                              <p className={`font-medium ${isMe ? 'text-amber-400' : 'text-white'}`}>
                                {entry.name} {isMe && '(You)'}
                              </p>
                              <p className="text-xs text-slate-500">{entry.city}{entry.parish ? `, ${entry.parish}` : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-right">
                            {entry.avg_rating > 0 && (
                              <div className="hidden sm:block">
                                <div className="flex items-center gap-1">
                                  <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                                  <span className="text-sm text-amber-400">{entry.avg_rating}</span>
                                </div>
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-bold text-white">{entry.packages_handled}</p>
                              <p className="text-xs text-slate-500">handled</p>
                            </div>
                            {entry.monthly_handled > 0 && (
                              <div className="hidden sm:block">
                                <div className="flex items-center gap-1 text-emerald-400">
                                  <TrendingUp className="h-3 w-3" />
                                  <span className="text-xs">{entry.monthly_handled} this month</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {leaderboard.leaderboard.length === 0 && (
                    <p className="text-center text-slate-500 py-6">No PUDO locations yet. Be the first!</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Become a Seller */}
            <Card className="bg-gradient-to-r from-emerald-900/30 to-amber-900/30 border-emerald-500/20" data-testid="become-seller-section">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-emerald-500/20 rounded-xl">
                      <Store className="h-7 w-7 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Become a Seller</h3>
                      <p className="text-sm text-slate-400">Start selling products from your PUDO location</p>
                    </div>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const res = await api.post('/role-requests/request-seller');
                        toast.success(res.data.message || 'Seller request submitted!');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to submit request');
                      }
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                    data-testid="apply-seller-btn"
                  >
                    <Store className="h-4 w-4 mr-2" />
                    Request Access
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Inventory List */}
          {waiting_packages.length > 0 && (
            <div className="space-y-3" data-testid="inventory-list">
              <h2 className="text-lg font-semibold text-white">Current Inventory ({waiting_packages.length})</h2>
              <div className="space-y-2">
                {waiting_packages.map((pkg, idx) => (
                  <Card
                    key={pkg.id || idx}
                    className="bg-slate-900 border-slate-800"
                    data-testid={`inventory-item-${idx}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-500/20 rounded-lg">
                          <Package className="h-5 w-5 text-amber-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{pkg.package_id?.slice(0, 8)}...</p>
                          <p className="text-sm text-slate-400">For: {pkg.recipient_name || 'Customer'}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate('/pudo/release', { state: { packageId: pkg.package_id } })}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        Release
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Incoming List */}
          {incoming_packages.length > 0 && (
            <div className="space-y-3" data-testid="incoming-list">
              <h2 className="text-lg font-semibold text-white">Incoming ({incoming_packages.length})</h2>
              <div className="space-y-2">
                {incoming_packages.map((pkg, idx) => (
                  <Card
                    key={pkg.id || idx}
                    className="bg-slate-900 border-slate-800"
                    data-testid={`incoming-item-${idx}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg">
                          <Truck className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{pkg.package_id?.slice(0, 8)}...</p>
                          <p className="text-sm text-slate-400">From: {pkg.from_driver_name || 'Driver'}</p>
                        </div>
                      </div>
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">En Route</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Settings className="h-5 w-5 text-amber-400" />
              PUDO Centre Settings
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure your PUDO centre details and location
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Opening Hours</Label>
              <Textarea
                value={pudoSettings.hours}
                onChange={(e) => setPudoSettings(prev => ({ ...prev, hours: e.target.value }))}
                placeholder="e.g., Mon-Fri 8:00 AM - 6:00 PM, Sat 9:00 AM - 2:00 PM"
                className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label className="text-slate-300">Address</Label>
              <Input
                value={pudoSettings.address}
                onChange={(e) => setPudoSettings(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Your PUDO centre address"
                className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label className="text-slate-300">Contact Phone</Label>
              <Input
                value={pudoSettings.phone}
                onChange={(e) => setPudoSettings(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 876 xxx xxxx"
                className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>

            <div>
              <Label className="text-slate-300 flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                GPS Coordinates
              </Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input
                  value={pudoSettings.latitude || ''}
                  onChange={(e) => setPudoSettings(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="Latitude"
                  readOnly
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <Input
                  value={pudoSettings.longitude || ''}
                  onChange={(e) => setPudoSettings(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="Longitude"
                  readOnly
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full mt-2 border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={getCurrentLocation}
                disabled={gettingLocation}
              >
                {gettingLocation ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Getting Location...</>
                ) : (
                  <><Navigation className="h-4 w-4 mr-2" />Capture Current Location</>
                )}
              </Button>
              {pudoSettings.latitude && pudoSettings.longitude && (
                <p className="text-xs text-emerald-400 mt-1 text-center">
                  Location captured: {pudoSettings.latitude}, {pudoSettings.longitude}
                </p>
              )}
            </div>

            <Button
              onClick={savePudoSettings}
              disabled={savingSettings}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {savingSettings ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Save Settings</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PudoDashboard;
